<template>
  <full-page id="fullpage"
             ref="fullpage"
             :options="options">
    <fin-full-page-section :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                           :style="`padding-bottom: ${fullpageBehaviourDisabled ? 3*finStrategistPaddingBottom : 0}px; margin-bottom: ${fullpageBehaviourDisabled ? 0-3*finStrategistPaddingBottom: 0}px`">
      <v-row class="justify-center pa-3 pb-12">
        <v-col align="center" class="pb-0" cols="12" lg="7" md="8">
          <h1>Strategie festlegen</h1>
          <p class="mt-5 mb-0">
            <fin-lexicon-text auto-solve text="
            Die Assetallokation beschreibt die Verteilung deines Startkapitals auf verschiedene Anlageklassen (Assets), wie Aktie, Anleihen, ETF, etc.
            Diese Vermögensaufteilung dient der Risikostreuung getreu dem Motto: „Lege nicht alle Eier in einen Korb!“">
            </fin-lexicon-text>
          </p>
          <p>
            <fin-lexicon-text auto-solve text="
            Die Assetallokation ist ein entscheidender Teil deiner Anlagestrategie.
            Passend zu deinem Archetypen wird dir im nächsten Schritt einen Vorschlag gemacht.
            Diesen kannst du übernehmen oder nach deinen Vorstellungen anpassen."/>
          </p>

          <v-btn dark @click="goToStrategieSlider()"
                 class="start-button mt-0"
                 style="z-index: 10; border-radius: 0">
            <span class="start-button-text pl-2 pr-2" style="color:white">Los geht's</span>
          </v-btn>

          <v-img :src="require('@/assets/onboarding/strategy/asset-verteilung.svg')"
                 contain
                 :height="$vuetify.breakpoint.xs ? '170px' : '273px'"
                 style="z-index: 1; margin-top: -28px;"
                 :width="$vuetify.breakpoint.xs ? '280px' : '449px'"/>
        </v-col>
      </v-row>
    </fin-full-page-section>

    <!-- the user defines his assets distribution -->
    <fin-full-page-section v-if="activeStep>=1"
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                           :style="`padding-bottom: ${fullpageBehaviourDisabled ? 3*finStrategistPaddingBottom : 0}px; margin-bottom: ${fullpageBehaviourDisabled ? 0-3*finStrategistPaddingBottom: 0}px`">
      <v-row class="justify-center pa-3 pb-12">
        <v-col align="center" cols="12" md="7" sm="10">
          <h1>
            <fin-lexicon-text auto-solve
                              text='Deine Vermögensaufteilung'/>
          </h1>
          <p class="mt-5" v-if="$vuetify.breakpoint.smAndUp">
            <fin-lexicon-text auto-solve
                              text='Aufgrund deines gewählten Archetyps schlagen wir dir folgende Verteilung der Assetklassen vor. Du kannst die Auswahl übernehmen oder durch Schieben der Regler ändern.'/>
          </p>
          <p class="mt-5" v-else>
            <fin-lexicon-text auto-solve
                              text='Dein Archetyp steht für folgende Verteilung der Assets. Du kanst sie durch Schieben der Regler auch ändern.'/>
          </p>

          <fin-strategy-slider :enable-cross="false"
                               :valueLabels="valueLabels"
                               @change="sliderChange()"
                               id="strategy-slider"
                               v-model="assetAllocation"/>

          <v-btn :disabled="!sliderChanged" @click="resetPortfolio" class="mr-4"
                 style="border-radius: 0; background-color: transparent;" text>
            <span class="pl-2 pr-2">Zurücksetzen</span>
            <v-icon>
              icon-refresh
            </v-icon>
          </v-btn>
          <v-btn :color="$finColors.primaryDark" @click="saveAssetAllocation"
                 class="start-button ml-4" style="border-radius: 0">
            <span class="start-button-text pl-2 pr-2" style="color:white">Übernehmen</span>
          </v-btn>
        </v-col>
      </v-row>
    </fin-full-page-section>

    <!-- the user defines his seed capital -->
    <fin-full-page-section v-if="activeStep>=2"
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                           :style="`padding-bottom: ${fullpageBehaviourDisabled ? 3*finStrategistPaddingBottom : 0}px; margin-bottom: ${fullpageBehaviourDisabled ? 0-3*finStrategistPaddingBottom: 0}px`">
      <v-row class="justify-center pa-3 pb-12">
        <v-col align="center" class="pb-0" cols="12" lg="7" md="8">
          <fin-investment-amount v-model="model.initialCapital"
                                 class="pa-3"
                                 :capital-limit1="capitalLimit1"
                                 :capital-limit2="capitalLimit2"
                                 @confirm="confirmCapital"
                                 @input="val => inputCapital(val)"
          />
        </v-col>
      </v-row>
    </fin-full-page-section>

    <!-- padding/margin workaround needed for xs to scroll down properly to the last section -->
    <fin-full-page-section v-if="activeStep>=3"
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                           class="pt-0"
                           :style="`background-color: ${$finColors.primaryDark}; padding-bottom: ${fullpageBehaviourDisabled ? 3*finStrategistPaddingBottom : finStrategistPaddingBottom}px; margin-bottom: ${fullpageBehaviourDisabled ? 0-3*finStrategistPaddingBottom : 0-finStrategistPaddingBottom}px`"
                           last-section>
      <!-- the user switches to step 4 -->
      <fin-go-to-next-step v-if="activeStep >= 3"
                           :button-disabled="model.initialCapital <= 0"
                           buttonBackgroundColor="#ffffff"
                           :buttonText="onboardingMode ? 'Weiter zu Schritt 3' : 'Weiter zum letzten Schritt'"
                           buttonTextColor="#02073a"
                           class="pb-4 px-3"
                           :style="'max-width: 600px; margin: 0 auto;'"
                           text="Jetzt wird es konkret: Wähle die Wertpapiere aus, die du kaufen willst. Und zwar für jede der von dir festgelegten Assetklassen."
                           textColor="#ffffff"
                           :title="onboardingMode ? 'Schritt 3 – Erstelle dein Portfolio' : 'Erstelle dein Portfolio'"
                           ref="nextStep"
                           titleColor="#ffffff"
                           @nextStep="previewMode ? showRegDialog() : nextStep()"
      />
    </fin-full-page-section>

    <fin-dialog v-model="showRegistrationDialog"
                :max-width="550">
      <template #title>
        Wie geht's weiter?
      </template>

      <p>Bevor du passende Wertpapiere für dein individuelles Portfolio auswählen kannst, musst du dich registrieren,
        um dir dein persönliches Nutzerkonto anzulegen.<br/><br/>Die Registrierung ist kostenlos, du gehst keine
        Verpflichtungen ein.</p>

      <template #actions>
        <v-btn @click="callRegistration">Jetzt kostenlos registrieren</v-btn>
      </template>
    </fin-dialog>
  </full-page>
</template>

<script>
import FinGoToNextStep from '@/components/pages/onboarding/GoToNextStep/index'
import FinStrategySlider from './StrategySlider/index'
import FinLexiconText from '@/components/common/lexicon/LexiconText'
import FinFullPageSection from '@/components/pages/onboarding/FullPageSection'
import assetClassesMock from '@/mocks/AssetClasses'
import {FULL_PAGE_KEY} from '@/js/finConstants'
import FinInvestmentAmount from '@/components/pages/onboarding/Step3/InvestmentAmount/index'
import finBrowser from "@/js/finBrowser"
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: 'fin-onboarding-step-3',
  mixins: [AmplitudePush],
  components: {
    FinInvestmentAmount,
    FinFullPageSection,
    FinStrategySlider,
    FinGoToNextStep,
    FinLexiconText
  },
  props: {
    value: {},
    previewOnboardingModel: {
      type: Object,
      default: null,
    },
  },
  data() {
    const value = Object.assign({
      assetAllocation: [20, 20, 20, 20, 20],
      initialCapital: -1,
      activeStep: 0
    }, this.$props.value)
    let assetAlloc = this.getAssetAllocationValuesAsArray(value.assetAllocation)

    return {
      context: this,
      initialAssetAllocation: assetAlloc,
      assetAllocation: assetAlloc,
      valueLabels: assetClassesMock.items.map(item => {
        if (item.id === 'COMMODITIES_ETF') {
          return 'Rohstoff-ETF'
        } else {
          return item.name
        }
      }),
      options: {
        licenseKey: FULL_PAGE_KEY,
        controlArrows: true,
        onLeave: this.onLeavePage,
        paddingTop: '0px',
        paddingBottom: '0px',
        responsiveWidth: 0,
        responsiveHeight: 0,
        scrollOverflow: false,
        // scrollOverflowReset: true,// only available with the fullpage-extensions!!
        // normalScrollElements is needed here, otherwise the slider's dots cannot be moved when the screen is not
        // high enought and a scrollbar is shown via fullpage
        normalScrollElements: '#strategy-slider'
      },
      sliderChanged: false,
      capitalChanged: false,
      model: value,
      onboardingMode: true,
      mode: undefined,
      fullpageBehaviourDisabled: false,
      previewMode: this.$route.path.includes('/preview'),
      showRegistrationDialog: false,
      capitalLimit1: 2500,
      capitalLimit2: 5000
    }
  },
  computed: {
    assetAllocationTarget: {
      get() {
        return this.model.assetAllocation
      },
      set(val) {
        this.$set(this.model, 'assetAllocation', val)
      }
    },
    activeStep: {
      get() {
        return this.model.activeStep
      },
      set(val) {
        this.$set(this.model, 'activeStep', val)
      }
    },
    onboardingPaddingTop() {
      let h = document.querySelector('.fin-onboarding-slider')?.getBoundingClientRect().height
      this.$set(this.options, 'paddingTop', (h ? h : 0) + 'px')
      return h
    },
    finStrategistPaddingBottom() {
      // needed because of footer in finStrategist-Mode
      let h = document.querySelector('.v-footer')?.getBoundingClientRect().height * 2
      if (this.$vuetify.breakpoint.xs) {
        h = document.querySelector('.v-bottom-navigation')?.getBoundingClientRect().height * 2
      }
      // in finStrategist on iPad there is no footer => a default needs to be set
      if (h === 0 || h === null) {
        h = 65 * 2
      }

      this.$set(this.options, 'paddingBottom', (h ? h : 0) + 'px')

      return h
    }
  },
  methods: {
    goToStrategieSlider() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_starting',
        'step': this.mode + "_strategy_slider",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.nextSection(1)
    },
    getAssetAllocationValuesAsArray(val) {
      return [
        val.find(me => me.name === "SHARE")?.percentage,
        val.find(me => me.name === "SHARE_ETF")?.percentage,
        val.find(me => me.name === "CORPORATE_BONDS_ETF")?.percentage,
        val.find(me => me.name === "GOVERNMENT_BONDS_ETF")?.percentage,
        val.find(me => me.name === "COMMODITIES_ETF")?.percentage
      ]
    },
    getAssetAllocationArray(val) {
      let assetClasses = ["SHARE", "SHARE_ETF", "CORPORATE_BONDS_ETF", "GOVERNMENT_BONDS_ETF", "COMMODITIES_ETF"]
      return assetClasses.map((item, index) => {
        return {name: assetClasses[index], percentage: val[index]}
      })
    },
    inputCapital(val) {
      this.model.initialCapital = val
      /*
      // When the user edits the capital, each change was being sent to amplitude. Unneeded information...
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_capital_entered',
        'step': this.mode + "_strategy_capital_confirm",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })*/
    },
    confirmCapital() {
      if (this.model.initialCapital < this.capitalLimit2) {
        this.assetAllocation = this.model.initialCapital < this.capitalLimit1 ? [0, 100, 0, 0, 0] : [50, 50, 0, 0, 0]
        this.assetAllocationTarget = this.getAssetAllocationArray(this.assetAllocation)
      }

      if (this.$refs.fullpage.api.getActiveSection().index !== 3) {
        this.activeStep = 3
        this.pushToAmplitude({
          'event': 'fincraft_' + this.mode + '_strategy_capital_confirmed',
          'step': this.mode + "_strategy_footer",
          'vue_breakpoint': this.$vuetify.breakpoint.name
        })
        this.$nextTick(() => {
          this.$refs.fullpage.build()
          this.$refs.fullpage.api.moveSectionDown()
        })
      }
    },
    nextStep() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_portfolio_starting',
        'step': this.mode + "_portfolio_start",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.$emit('nextStep')
    },
    onLeavePage(origin, destination) {
      this.$emit('pageChange', destination.index)
    },
    scrollToSection(section) {
      this.$refs?.fullpage?.api.silentMoveTo(section)
    },
    nextSection(index) {
      if (this.activeStep < index) {
        this.activeStep = index
      }
      this.nextDynamicPage()
    },
    saveAssetAllocation() {
      this.assetAllocationTarget = this.getAssetAllocationArray(this.assetAllocation)
      this.sliderChanged = false
      // TODO Kapital zurücksetzen
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_slider_values_saved',
        'step': this.mode + "_strategy_capital",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.nextSection(2)
    },
    resetPortfolio() {
      this.assetAllocation = this.initialAssetAllocation
      this.sliderChanged = false
      // TODO Kapital zurücksetzen
    },
    sliderChange() {
      this.sliderChanged = true
      // TODO Kapital zurücksetzen
    },
    nextDynamicPage() {
      this.$nextTick(() => {
        this.$refs.fullpage.build()
        this.$refs.fullpage.api.moveSectionDown()
      })
    },
    showRegDialog() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_show_registration_dialog',
        'step': this.mode + "_strategy_registration_dialog",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.showRegistrationDialog = true
    },
    callRegistration() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_strategy_call_registration',
        'step': "registration",
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      // set currentState to 3 to make sure, the user is redirected to the step portfolio after his account has been verified
      // TODO fix unexpected mutation of prop - is previewOnboardingModel still used after removing preview functionality?
      this.previewOnboardingModel.currentState = 3
      sessionStorage.setItem('onboarding', JSON.stringify(this.previewOnboardingModel))
      this.$router.push({name: 'onbregstart', params: {register: true}})
    }
  },
  mounted() {
    // disable fullpage behaviour in mobiles and tablets
    this.fullpageBehaviourDisabled = finBrowser.IS_MOBILE_OR_TABLET || finBrowser.IS_IOS_OR_ANDROID_APP
    this.$set(this.options, 'responsiveWidth', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'responsiveHeight', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'scrollOverflow', !this.fullpageBehaviourDisabled)

    this.$set(this.options, 'paddingTop', (this.onboardingPaddingTop ? this.onboardingPaddingTop + 'px' : '0px'))

    if (this.$route.fullPath.includes('finStrategist')) {
      this.$set(this.options, 'paddingBottom', this.finStrategistPaddingBottom)
    }

    this.onboardingMode = !this.$route.fullPath.includes('finStrategist')
    this.mode = this.$route.fullPath.includes('finStrategist') ? 'finStrategist' : 'onboarding'

    this.scrollToSection(this.activeStep >= 3 ? 3 : this.activeStep)
  },
  watch: {
    model: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true
    },
  },
  destroyed() {
    //workaround for FINDEV-2514
    document.body.style.overflow = null
  },
}

</script>

<style lang="scss" scoped>
@import "../../../../styles/variables";

.start-button {
  color: #ffffff;
  background-color: #02073a;
}

.invest-expl {
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  letter-spacing: normal;
}

.invest-nb {
  font-family: $heading-font-family;
  font-size: 32px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.19;
  letter-spacing: normal;
}

.capital-illu {
  margin-left: -465px;
  margin-bottom: -20px;
}

.capital-inputfield {
  width: 272px;

  ::v-deep .v-input__slot {
    box-shadow: $fin-color-shadow;
    border: solid 1px #cfd1d3;
    background-color: #ffffff;
  }
}

::v-deep .vue-slider-disabled {
  opacity: 1;
  cursor: default;
}

.col {
  padding-bottom: 0;
}

::v-deep .capital-inputfield input {
  text-align: right !important;
}

.capital-inputfield ::v-deep .v-text-field__suffix {
  padding-left: 0;
  color: black;
}

</style>
